import { Divider, Group, Select } from '@byecode/ui'
import {
    emptyNumberField,
    emptyTextField,
    type FlowNode,
    getInputInnerTypeMatch,
    getUpstreamNodesOptions,
    ListItem4ByecodeUi,
    NodeFieldPreview,
    nodeTypeOptions,
    type NodeTypes,
    OtherPlatformNodeType,
    OUTPUT_SINGLE_DATA_NODES,
    useAtomData,
    type WechatGetOrderListActionConfig
} from '@lighthouse/shared'
import React, { useMemo } from 'react'

import { otherPlatformListAtom } from '@/atoms/application/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetUserRecordOptions } from '@/hooks/usePage'

import OutputVariable from '../../../OutputVariable'
import { UnitNoAuth } from '../../Common/UnitNoAuth'
import * as CM from '../../styles'
import { ORDER_STATE_OPTIONS } from '../wechatShipping/constants'

interface WechatGetOrderListActionConfigureProps {
    nodeType?: NodeTypes
    allParentNodes: FlowNode[]
    actionTypeSwitcher?: React.ReactNode
    value: WechatGetOrderListActionConfig
    onChange: (value: WechatGetOrderListActionConfig) => void
}

export const WechatGetOrderListActionConfigure: React.FC<WechatGetOrderListActionConfigureProps> = ({
    nodeType,
    allParentNodes,
    actionTypeSwitcher,
    value,
    onChange
}) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const otherPlatformList = useAtomData(otherPlatformListAtom)
    const userOption = useGetUserRecordOptions()
    const miniProgramAccount = useMemo(
        () => otherPlatformList?.find(item => item.type === 'WECHAT_MINI_PROGRAM'),
        [otherPlatformList]
    )

    const textUpstreamOptions = useMemo(
        () =>
            getUpstreamNodesOptions({
                workflowList,
                dataSourceList,
                upstreamNodes: allParentNodes.filter(node => OUTPUT_SINGLE_DATA_NODES.has(node.data.nodeType)),
                allUpstreamNodes: allParentNodes
            }),
        [allParentNodes, dataSourceList, workflowList]
    )

    const header = (
        <Group label="动作配置">
            <ListItem4ByecodeUi>
                <CM.FormItemLabelWrapper>
                    <CM.FormItemLabel>类型</CM.FormItemLabel>
                </CM.FormItemLabelWrapper>
                <CM.FormItemContent>
                    {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                </CM.FormItemContent>
            </ListItem4ByecodeUi>
        </Group>
    )

    if (!miniProgramAccount) {
        return (
            <>
                {header}
                <UnitNoAuth type="WECHAT_MINI_PROGRAM" />
            </>
        )
    }

    return (
        <CM.Container>
            {header}
            <Divider />
            <Group label="查询条件">
                <CM.FieldList>
                    <CM.FieldItem>
                        <CM.FieldTitle required>支付者 openid</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.openid}
                            onBlur={openid => onChange({ ...value, openid })}
                            autoHeight
                            placeholder="通常绑定当前用户小程序 OpenID"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle>订单状态</CM.FieldTitle>
                        <Select
                            options={ORDER_STATE_OPTIONS}
                            value={value.orderState ? String(value.orderState) : ''}
                            onChange={orderState =>
                                onChange({
                                    ...value,
                                    orderState: orderState ? Number(orderState) : undefined
                                })
                            }
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle>支付开始时间</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.payTimeBegin}
                            onBlur={payTimeBegin => onChange({ ...value, payTimeBegin })}
                            autoHeight
                            placeholder="时间戳或日期变量，可选"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle>支付结束时间</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.payTimeEnd}
                            onBlur={payTimeEnd => onChange({ ...value, payTimeEnd })}
                            autoHeight
                            placeholder="时间戳或日期变量，可选"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle>每页数量</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('NUMBER')}
                            disabled={disabledWithVersion}
                            field={emptyNumberField}
                            value={value.pageSize}
                            onBlur={pageSize => onChange({ ...value, pageSize })}
                            autoHeight
                            placeholder="默认 100，最大 100"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                    <CM.FieldItem>
                        <CM.FieldTitle>翻页游标</CM.FieldTitle>
                        <NodeFieldPreview
                            innerTypes={getInputInnerTypeMatch('TEXT')}
                            disabled={disabledWithVersion}
                            field={emptyTextField}
                            value={value.lastIndex}
                            onBlur={lastIndex => onChange({ ...value, lastIndex })}
                            autoHeight
                            placeholder="上一页输出的 lastIndex，可选"
                            userOption={userOption}
                            upstreamOptions={textUpstreamOptions}
                            dataSourceList={dataSourceList}
                        />
                    </CM.FieldItem>
                </CM.FieldList>
            </Group>
            <Divider />
            <OutputVariable nodeType={OtherPlatformNodeType.WECHAT_GET_ORDER_LIST} />
        </CM.Container>
    )
}
